
import React, { useState, useEffect } from 'react';
import { Box, Typography, Avatar, Button, TextField, Dialog, DialogTitle, DialogContent, DialogActions, Switch, FormControlLabel, MenuItem, Menu, IconButton, useTheme } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import EmailIcon from '@mui/icons-material/Email';
import LockIcon from '@mui/icons-material/Lock';
import SecurityIcon from '@mui/icons-material/Security';
import LogoutIcon from '@mui/icons-material/Logout';
import api from '../services/api';
import NotificationSnackbar from '../components/NotificationSnackbar';
import AdminLayout from '../layouts/AdminLayout';
import SuperAdminLayout from '../layouts/SuperAdminLayout';
import StaffLayout from '../layouts/StaffLayout';

function ProfilePage() {
  const [user, setUser] = useState({});
  const [editDialog, setEditDialog] = useState(false);
  const [editField, setEditField] = useState('');
  const [editValue, setEditValue] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [anchorEl, setAnchorEl] = useState(null);
  const [logs, setLogs] = useState([]);
  const [show2FA, setShow2FA] = useState(false);
  const [showRecovery, setShowRecovery] = useState(false);
  const [showComms, setShowComms] = useState(false);

  useEffect(() => {
    api.get('/api/user/profile')
      .then(res => setUser(res.data))
      .catch(err => setSnackbar({ open: true, message: err.response?.data?.error || 'Failed to fetch profile', severity: 'error' }));
    api.get('/api/logs/user')
      .then(res => setLogs(res.data))
      .catch(() => {});
  }, []);

  const handleEdit = (field) => {
    setEditField(field);
    setEditValue(user[field] || '');
    setEditDialog(true);
  };

  const handleEditSave = async () => {
    try {
      await api.put('/api/user/profile', { [editField]: editValue });
      setUser(prev => ({ ...prev, [editField]: editValue }));
      setSnackbar({ open: true, message: 'Profile updated', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'Update failed', severity: 'error' });
    }
    setEditDialog(false);
  };

  const handleLogout = () => {
    setAnchorEl(null);
    if (window.confirm('Are you sure you want to log out?')) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
  };

  // Choose layout based on user role
  const theme = useTheme();
  const getLayout = (children) => {
    if (user.role === 'superadmin') {
      return (
        <SuperAdminLayout>
          <Box sx={{ maxWidth: 700, mx: 'auto', mt: 4, p: { xs: 2, md: 4 }, boxShadow: 3, borderRadius: 4, bgcolor: theme.palette.mode === 'dark' ? 'background.paper' : '#fff',
            transition: 'background 0.3s', minHeight: 500 }}>
            {children}
          </Box>
        </SuperAdminLayout>
      );
    }
    if (user.role === 'admin') {
      return (
        <AdminLayout>
          <Box sx={{ maxWidth: 700, mx: 'auto', mt: 4, p: { xs: 2, md: 4 }, boxShadow: 3, borderRadius: 4, bgcolor: theme.palette.mode === 'dark' ? 'background.paper' : '#fff',
            transition: 'background 0.3s', minHeight: 500 }}>
            {children}
          </Box>
        </AdminLayout>
      );
    }
    if (user.role === 'staff') {
      return (
        <StaffLayout>
          <Box sx={{ maxWidth: 700, mx: 'auto', mt: 4, p: { xs: 2, md: 4 }, boxShadow: 3, borderRadius: 4, bgcolor: theme.palette.mode === 'dark' ? 'background.paper' : '#fff',
            transition: 'background 0.3s', minHeight: 500 }}>
            {children}
          </Box>
        </StaffLayout>
      );
    }
    return <Box sx={{ width: '100vw', minHeight: '100vh', bgcolor: 'background.default' }}>{children}</Box>;
  };

  return getLayout(
    <>
      <Typography variant="h4" fontWeight="bold" sx={{ mb: 2, letterSpacing: 1, color: 'primary.main' }}>
        Profile
      </Typography>
      <Box sx={{ borderBottom: 2, borderColor: 'divider', mb: 3 }} />
      {/* Professional summary card for role and permissions */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Avatar sx={{ width: 72, height: 72, mr: 2, bgcolor: theme.palette.primary.main, color: theme.palette.primary.contrastText, fontWeight: 700, fontSize: 36 }}>{user.name?.[0]}</Avatar>
        <Box>
          <Typography variant="h5" fontWeight="bold">{user.name}</Typography>
          <Typography color="text.secondary" sx={{ textTransform: 'capitalize', fontWeight: 600 }}>{user.role}</Typography>
          <Box sx={{ mt: 1, p: 1.5, bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'grey.100', borderRadius: 2, boxShadow: 1 }}>
            <Typography variant="caption" color="primary" fontWeight="bold">Panel Access:</Typography>
            <ul style={{ margin: 0, paddingLeft: 18, fontWeight: 500 }}>
              {user.role === 'superadmin' && <li>Super Admin Panel (all permissions)</li>}
              {user.role === 'admin' && <li>Admin Panel (manage events, users, bookings, tickets, payments, CMS, reports, settings)</li>}
              {user.role === 'staff' && <li>Staff Panel (manage bookings, tickets, notifications, CMS, settings)</li>}
              {user.role === 'user' && <li>User Portal (view bookings, tickets, profile)</li>}
            </ul>
            <Typography variant="caption" color="text.secondary">
              {user.role === 'superadmin' && 'You have full access to all system features, user management, and settings.'}
              {user.role === 'admin' && 'You can manage all event operations except superadmin-only features.'}
              {user.role === 'staff' && 'You can manage bookings, tickets, notifications, and CMS content.'}
              {user.role === 'user' && 'You can view your bookings, tickets, and update your profile.'}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ flexGrow: 1 }} />
        <IconButton onClick={e => setAnchorEl(e.currentTarget)}>
          <LogoutIcon />
        </IconButton>
        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
          <MenuItem onClick={handleLogout}>Log Out</MenuItem>
        </Menu>
      </Box>
      <NotificationSnackbar
        open={snackbar.open}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        message={snackbar.message}
        severity={snackbar.severity}
      />
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle1" fontWeight="bold">Email</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <EmailIcon sx={{ mr: 1 }} />
          <Typography>{user.email}</Typography>
          <IconButton size="small" onClick={() => handleEdit('email')}><EditIcon fontSize="small" /></IconButton>
        </Box>
      </Box>
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle1" fontWeight="bold">Password</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <LockIcon sx={{ mr: 1 }} />
          <Button size="small" onClick={() => handleEdit('password')}>Change Password</Button>
        </Box>
      </Box>
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle1" fontWeight="bold">2FA</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <SecurityIcon sx={{ mr: 1 }} />
          <FormControlLabel control={<Switch checked={show2FA} onChange={() => setShow2FA(v => !v)} />} label="Enable 2FA" />
        </Box>
      </Box>
      <Box sx={{ mb: 2 }}>
        <Button variant="outlined" onClick={() => setShowRecovery(v => !v)} sx={{ mr: 2 }}>Recovery Options</Button>
        <Button variant="outlined" onClick={() => setShowComms(v => !v)}>Staff/Admin Communication</Button>
      </Box>
      {showRecovery && (
        <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.100', borderRadius: 2 }}>
          <Typography>Recovery email: {user.recoveryEmail || 'Not set'}</Typography>
          <Button size="small" onClick={() => handleEdit('recoveryEmail')}>Edit Recovery Email</Button>
        </Box>
      )}
      {showComms && (
        <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.100', borderRadius: 2 }}>
          <Typography>Contact your admin or manager for support. (Chat/Comms coming soon)</Typography>
        </Box>
      )}
      <Box sx={{ mt: 4 }}>
        <Typography variant="h6" fontWeight="bold">Profile Logs</Typography>
        <Box sx={{ maxHeight: 120, overflow: 'auto', bgcolor: 'grey.50', borderRadius: 2, p: 1 }}>
          {logs.length === 0 ? <Typography color="text.secondary">No logs found.</Typography> : logs.map((log, i) => (
            <Typography key={i} variant="body2">{log.message} <span style={{ color: '#888', fontSize: 12 }}>({log.date})</span></Typography>
          ))}
        </Box>
      </Box>
      <Dialog open={editDialog} onClose={() => setEditDialog(false)}>
        <DialogTitle>Edit {editField}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={editField}
            type={editField === 'password' ? 'password' : 'text'}
            fullWidth
            value={editValue}
            onChange={e => setEditValue(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog(false)}>Cancel</Button>
          <Button onClick={handleEditSave} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default ProfilePage;
